import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import { Card, Heading, HeadingVariants, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { selectHeardProductSubscription } from '@/reducers/subscription.slice'
import {
  fetchCardSummary,
  fetchOpenInvoiceRetry,
} from '@/features/billing/payment-method/paymentMethodActions'
import { usePaymentMethodUpdate } from '@/features/billing/payment-method/usePaymentMethodUpdate'
import { PaymentMethodForm } from '@/features/billing/payment-method/PaymentMethodForm'
import { cardNameLabel } from '@/features/billing/payment-method/paymentMethodCopy'
import type { CardSummary } from '@/features/billing/payment-method/payment-method.types'
import { BillingSubpageBreadcrumb } from './BillingSubpageBreadcrumb'
import { PLAN_AND_BILLING_PATH } from './paths'

/** `null` means the server has not answered, so the page makes no charge claim. */
type WillRetry = boolean | null

const chargeTrailer = (willRetry: WillRetry): string => {
  if (willRetry === null) return ''
  return willRetry ? ' Open invoices are retried.' : ' Nothing is charged now.'
}

const formDescription = (
  card: CardSummary | null,
  nextBillingDate: string | null,
  willRetry: WillRetry,
  cardFetchFailed: boolean
): string => {
  const trailer = chargeTrailer(willRetry)
  if (cardFetchFailed)
    return `Replaces your card on file for all Heard billing.${trailer}`
  if (!card) return `Adds a card for all Heard billing.${trailer}`
  const starting =
    willRetry === false && nextBillingDate
      ? `, starting with your ${nextBillingDate} invoice`
      : ''
  return `Replaces ${cardNameLabel(card)} for all Heard billing${starting}.${trailer}`
}

export const PaymentMethodPage = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const subscription = useReselector(selectHeardProductSubscription)
  const [card, setCard] = useState<CardSummary | null>(null)
  const [cardFetchFailed, setCardFetchFailed] = useState(false)
  const [willRetry, setWillRetry] = useState<WillRetry>(null)

  const goToPlanAndBilling = (savedCard: CardSummary | null = null): void => {
    navigate(PLAN_AND_BILLING_PATH, {
      state: savedCard ? { savedCard } : undefined,
    })
  }

  const update = usePaymentMethodUpdate({
    surface: 'plan and billing',
    onSaved: goToPlanAndBilling,
  })

  useEffect(() => {
    update.open()
    // Opens the SetupIntent session once, on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchCardSummary()(dispatch)
      .then((summary) => {
        setCard(summary)
      })
      .catch(() => {
        setCard(null)
        setCardFetchFailed(true)
      })
  }, [dispatch])

  useEffect(() => {
    fetchOpenInvoiceRetry()(dispatch)
      .then((result) => setWillRetry(result.willRetry))
      .catch(() => setWillRetry(null))
  }, [dispatch])

  const nextBillingDate = subscription?.nextBillingDate
    ? DateTime.fromSeconds(subscription.nextBillingDate).toFormat(
        DATE_FORMATS_LUXON.DISPLAY_LONG
      )
    : null

  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-6 p-8">
      <BillingSubpageBreadcrumb currentPage="Payment method" />
      <Card className="p-8" childrenClassName="flex flex-col gap-6">
        <div className="flex flex-col gap-1">
          <Heading variant={HeadingVariants.H2}>Update payment method</Heading>
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            {formDescription(card, nextBillingDate, willRetry, cardFetchFailed)}
          </Text>
        </div>
        <PaymentMethodForm
          update={update}
          flow="account"
          currentCard={card}
          onCancel={() => goToPlanAndBilling()}
        />
      </Card>
    </div>
  )
}
